import { useCallback, useEffect, useRef, useState } from 'react'

const LS_INPUT = 'kordai.midi.inputId'
const LS_OUTPUT = 'kordai.midi.outputId'

function readLs(key) {
  try {
    const v = localStorage.getItem(key)
    return v && v.length ? v : ''
  } catch {
    return ''
  }
}

function writeLs(key, value) {
  try {
    if (!value) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch { /* ignore */ }
}

function listPorts(map) {
  const out = []
  for (const port of map.values()) {
    out.push({
      id: port.id,
      name: port.name || port.id,
      manufacturer: port.manufacturer || '',
      state: port.state,
    })
  }
  out.sort((a, b) => a.name.localeCompare(b.name, 'ko'))
  return out
}

/**
 * Web MIDI 입·출력 장치 열거·선택·노트 송신.
 * 선택값은 localStorage에 유지.
 */
export function useMidiDevices() {
  const [status, setStatus] = useState('idle') // idle | unsupported | pending | ready | error
  const [inputs, setInputs] = useState([])
  const [outputs, setOutputs] = useState([])
  const [inputId, setInputIdState] = useState(() => readLs(LS_INPUT))
  const [outputId, setOutputIdState] = useState(() => readLs(LS_OUTPUT))
  const accessRef = useRef(null)

  const refreshPorts = useCallback((midiAccess) => {
    const nextIn = listPorts(midiAccess.inputs)
    const nextOut = listPorts(midiAccess.outputs)
    setInputs(nextIn)
    setOutputs(nextOut)

    setInputIdState((prev) => {
      if (prev && nextIn.some((p) => p.id === prev)) return prev
      const fallback = nextIn[0]?.id || ''
      writeLs(LS_INPUT, fallback)
      return fallback
    })
    setOutputIdState((prev) => {
      if (prev && nextOut.some((p) => p.id === prev)) return prev
      // 출력은 선택 안 함이 기본(샘플러만 사용). 저장된 값이 없어진 경우만 비움.
      if (prev && !nextOut.some((p) => p.id === prev)) {
        writeLs(LS_OUTPUT, '')
        return ''
      }
      return prev
    })

    setStatus(nextIn.length || nextOut.length ? 'ready' : 'pending')
  }, [])

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.requestMIDIAccess) {
      setStatus('unsupported')
      return undefined
    }

    let cancelled = false
    setStatus('pending')
    navigator.requestMIDIAccess({ sysex: false })
      .then((midiAccess) => {
        if (cancelled) return
        accessRef.current = midiAccess
        refreshPorts(midiAccess)
        midiAccess.onstatechange = () => {
          if (!cancelled && accessRef.current) refreshPorts(accessRef.current)
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('[midi]', err)
        setStatus('error')
        setInputs([])
        setOutputs([])
      })

    return () => {
      cancelled = true
      if (accessRef.current) accessRef.current.onstatechange = null
      accessRef.current = null
    }
  }, [refreshPorts])

  const setInputId = useCallback((id) => {
    const next = id || ''
    writeLs(LS_INPUT, next)
    setInputIdState(next)
  }, [])

  const setOutputId = useCallback((id) => {
    const next = id || ''
    writeLs(LS_OUTPUT, next)
    setOutputIdState(next)
  }, [])

  const refresh = useCallback(() => {
    if (accessRef.current) refreshPorts(accessRef.current)
  }, [refreshPorts])

  const getOutput = useCallback(() => {
    const access = accessRef.current
    if (!access || !outputId) return null
    return access.outputs.get(outputId) || null
  }, [outputId])

  const sendNoteOn = useCallback((midi, velocity = 100) => {
    const out = getOutput()
    if (!out || !Number.isFinite(midi)) return
    const v = Math.max(1, Math.min(127, Math.round(velocity)))
    try {
      out.send([0x90, Math.round(midi) & 0x7f, v])
    } catch (e) {
      console.warn('[midi-out]', e)
    }
  }, [getOutput])

  const sendNoteOff = useCallback((midi) => {
    const out = getOutput()
    if (!out || !Number.isFinite(midi)) return
    try {
      out.send([0x80, Math.round(midi) & 0x7f, 0])
    } catch (e) {
      console.warn('[midi-out]', e)
    }
  }, [getOutput])

  const inputName = inputs.find((p) => p.id === inputId)?.name || null
  const outputName = outputs.find((p) => p.id === outputId)?.name || null

  return {
    status,
    inputs,
    outputs,
    inputId,
    outputId,
    setInputId,
    setOutputId,
    refresh,
    sendNoteOn,
    sendNoteOff,
    inputName,
    outputName,
  }
}
